/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useMemo } from "react";
import styles from "./roles-selector.module.scss";
import checkedCheckboxIcon from "@src/assets/svg/checked-checkbox-icon.svg";
import checkboxIcon from "@src/assets/svg/checkbox-icon.svg";
import caretDownIcon from "@src/assets/svg/caret-down.svg";
import { warningSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import RolePill from "@src/components/atoms/role-pill/role-pill";
import { ZitadelServiceUser, ZitadelUser } from "@src/types/user-management";
import { useClickOutsideListener } from "@src/hooks/use-click-outside-listener";
import { PillsMeasurement, useVisiblePills } from "./use-visible-pills";
import { useI18N } from "@src/language/language-context";
import { RootState } from "@src/reducers/store";
import { useSelector } from "react-redux/es/hooks/useSelector";
import questionMarkIcon from "@src/assets/svg/question-mark-full-gray-12.svg";
import { RolesDescriptionPopover } from "@src/components/molecules/roles-description-popover/roles-description-popover";

interface RolesSelectorProps {
  user: Partial<ZitadelUser> | Partial<ZitadelServiceUser> | null;
  setUser: React.Dispatch<
    React.SetStateAction<
      Partial<ZitadelUser> | Partial<ZitadelServiceUser> | null
    >
  >;
  roleOptions: string[];
}

const cssClassName = "roles-selector";

export const RoleSelector = ({
  user,
  setUser,
  roleOptions,
}: RolesSelectorProps) => {
  const i18n = useI18N();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  useClickOutsideListener(dropdownRef, () => setIsOpen(false), isOpen);

  const rolesDescriptions = useSelector(
    (state: RootState) => state.yourSlice.rolesDescriptions,
  );

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const texts = {
    rolesLabel: i18n("modules.rolesSelector.rolesLabel"),
    rolesWarningText: i18n("modules.rolesSelector.rolesWarningText"),
    noRolesText: i18n("modules.rolesSelector.noRolesText"),
    rolesDescriptionPopoverAltText: i18n(
      "modules.usersList.rolesDescriptionPopoverAltText",
    ),
  };

  const roles = useMemo(() => user?.roles || [], [user?.roles]);

  const {
    containerRef: pillsContainerRef,
    measureRef,
    visibleCount,
  } = useVisiblePills({ items: roles });

  const handleUserRoles = (role: string) => {
    if (!user) return;

    const currentRoles = user.roles || [];

    if (currentRoles.includes(role)) {
      if (currentRoles.length > 1) {
        setUser({ ...user, roles: currentRoles.filter((r) => r !== role) });
      } else {
        warningSnackbar(texts.rolesWarningText);
      }
    } else {
      setUser({ ...user, roles: [...currentRoles, role] });
    }
  };

  // we only want to show roles that are in the roleOptions list
  const displayedRoles = roles
    .filter((role) => roleOptions.includes(role))
    .slice(0, visibleCount);
  const hasMoreRoles = visibleCount < roles.length;

  return (
    <div
      className={styles[cssClassName]}
      onPointerLeave={() => setIsPopoverOpen(false)}
    >
      <div className={styles[`${cssClassName}__label-container`]}>
        <p className={styles[`${cssClassName}__label`]}>{texts.rolesLabel}</p>
        {Object.keys(rolesDescriptions).length && (
          <>
            <img
              src={questionMarkIcon}
              alt={texts.rolesDescriptionPopoverAltText}
              className={styles[`${cssClassName}__roles-icon`]}
              onPointerEnter={() => {
                setIsPopoverOpen(true);
              }}
            />

            {isPopoverOpen && (
              <RolesDescriptionPopover
                rolesDescriptions={rolesDescriptions}
                customTopPositioning={20}
              />
            )}
          </>
        )}
      </div>
      <div className={styles[`${cssClassName}__selector`]} ref={dropdownRef}>
        <div
          className={styles[`${cssClassName}__header`]}
          onClick={() => setIsOpen(!isOpen)}
        >
          <div
            className={styles[`${cssClassName}__pills-container`]}
            ref={pillsContainerRef}
          >
            {displayedRoles.map((role) => (
              <RolePill key={role} role={role} />
            ))}
            {hasMoreRoles && (
              <div className={styles[`${cssClassName}__pill`]}>...</div>
            )}
          </div>

          <img
            className={styles[`${cssClassName}__caret`]}
            src={caretDownIcon}
            alt="Caret Down"
          />
        </div>

        {/* hidden measurement container used to calculate visible pills - do NOT remove */}
        <PillsMeasurement
          items={roles}
          measureRef={measureRef}
          cssClassName={cssClassName}
        />

        {isOpen && (
          <div className={styles[`${cssClassName}__dropdown`]}>
            <div className={styles[`${cssClassName}__dropdown-inner`]}>
              {roleOptions.length > 0 ? (
                roleOptions.map((role) => {
                  const isChecked = user?.roles?.includes(role) || false;
                  return (
                    <div
                      key={role}
                      className={styles[`${cssClassName}__option`]}
                      onClick={() => handleUserRoles(role)}
                    >
                      <img
                        src={isChecked ? checkedCheckboxIcon : checkboxIcon}
                        alt="Checkbox"
                      />
                      <span
                        className={styles[`${cssClassName}__checkbox-label`]}
                      >
                        {role}
                      </span>
                    </div>
                  );
                })
              ) : (
                <div className={styles[`${cssClassName}__text`]}>
                  {texts.noRolesText}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
